import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import { GitBranch, ChevronLeft, ChevronRight } from "lucide-react";
import type { TimelineItem } from "@/models/legislation/Timeline";
import type { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import {
  getZoomLevelNodes,
  cleanTitle,
  type BranchInfo,
} from "./compactTimelineUtils";
import { formatDanishDate, parseSignatureDate } from "@/lib/timelineDates";
import {
  centerTimelineNodeInView,
  getHorizontalScrollOverflow,
  getTimelineNodeTargetScrollLeft,
  isElementInHorizontalScrollViewport,
  scrollTimelineByOneStep,
  scrollTimelineNodeToCenter,
} from "./timelineStripLayout";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import type { ConsolidationProgressDisplay } from "@/lib/consolidationProgressDisplay";
import { flattenTimeline } from "@/lib/diffViewHtml";
import {
  getCircleFillClass,
  getDiamondFillClass,
  isCircleSelected,
  isDiamondSelected,
  resolveConsolidationAsOfForInbound,
  resolveInboundForTimelineNode,
  resolveInboundForTimelineNodeAcrossWindows,
  TIMELINE_COMPARE_RING,
  TIMELINE_OUT_OF_ORDER_CIRCLE,
  TIMELINE_OUT_OF_ORDER_LABEL,
  TIMELINE_SELECTION_RING,
  type TimelineSelection,
} from "./timelineNodeAppearance";

type TimelineStripProps = {
  root: TimelineItem;
  currentAccessionNumber: string;
  currentFlatTimelineIndex?: number | null;
  timelineSelection?: TimelineSelection | null;
  onNodeClick: (
    item: TimelineItem,
    event?: React.MouseEvent,
    flatTimelineIndex?: number
  ) => void;
  compareMode?: boolean;
  compareTargetAccessionNumber?: string | null;
  /**
   * When provided, only these accessions are selectable compare targets (in
   * either direction). When null/undefined, falls back to "earlier only".
   */
  eligibleCompareAccessions?: Set<string> | null;
  inboundChanges?: InboundChange[];
  /** Inbound changes per LBK window anchor, for plotting diamonds across all windows. */
  inboundByAnchor?: ReadonlyMap<string, ReadonlyArray<InboundChange>>;
  anchorConsolidationProgress?: ConsolidationProgressDisplay | null;
  anchorLawAccession?: string;
  showConsolidationDiamonds?: boolean;
  onOpenConsolidation?: (
    changelawAccession: string,
    options: {
      asOf: string | null;
      flatTimelineIndex: number;
    },
    event?: React.MouseEvent
  ) => void;
  onActiveOffScreenChange?: (offScreen: boolean) => void;
  /**
   * Accessions to wash with the selection tint (consolidation prioritization
   * scope preview). Purely decorative — does not affect clickability.
   */
  highlightedAccessions?: ReadonlySet<string> | null;
};

export type TimelineStripHandle = {
  centerOnActive: () => void;
};

type HoveredNode = {
  item: TimelineItem;
  x: number;
  y: number;
  inboundEntry?: InboundChange;
} | null;

type ScrollOverflow = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
};

const formatVerifiedDate = (isoDate: string | undefined): string | null => {
  if (!isoDate) return null;
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("da-DK");
};

/** Top padding when consolidation diamond row is reserved above timeline nodes. */
const TIMELINE_DIAMOND_ROW_PADDING_CLASS = "pt-10";
const TIMELINE_DEFAULT_ROW_PADDING_CLASS = "pt-1.5";

const TimelineStrip = forwardRef<TimelineStripHandle, TimelineStripProps>(
  function TimelineStrip(
    {
      root,
      currentAccessionNumber,
      currentFlatTimelineIndex = null,
      timelineSelection = null,
      onNodeClick,
      compareMode = false,
      compareTargetAccessionNumber,
      eligibleCompareAccessions = null,
      inboundChanges,
      inboundByAnchor,
      anchorConsolidationProgress = null,
      anchorLawAccession,
      showConsolidationDiamonds = false,
      onOpenConsolidation,
      onActiveOffScreenChange,
      highlightedAccessions = null,
    },
    ref
  ) {
  const [hoveredNode, setHoveredNode] = useState<HoveredNode>(null);
  const [branchPopoverOpen, setBranchPopoverOpen] = useState(false);
  const [scrollOverflow, setScrollOverflow] = useState<ScrollOverflow>({
    canScrollLeft: false,
    canScrollRight: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentNodeRef = useRef<HTMLDivElement>(null);
  const prevScrollKeyRef = useRef<string | null>(null);
  const isActiveOffScreenRef = useRef(false);
  const onActiveOffScreenChangeRef = useRef(onActiveOffScreenChange);
  onActiveOffScreenChangeRef.current = onActiveOffScreenChange;

  const { allNodes, currentIndex, branchPoint } = useMemo(
    () =>
      getZoomLevelNodes(
        root,
        currentAccessionNumber,
        currentFlatTimelineIndex
      ),
    [root, currentAccessionNumber, currentFlatTimelineIndex]
  );

  const flatTimeline = useMemo(() => flattenTimeline([root]), [root]);

  const timelineStripScrollKey = useMemo(
    () =>
      [
        root.accessionNumber ?? "",
        allNodes.length,
        currentIndex,
        currentAccessionNumber,
        currentFlatTimelineIndex ?? "",
      ].join(":"),
    [
      allNodes.length,
      currentAccessionNumber,
      currentFlatTimelineIndex,
      currentIndex,
      root.accessionNumber,
    ]
  );

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    const currentNode = currentNodeRef.current;
    if (!container) return;
    setScrollOverflow(getHorizontalScrollOverflow(container));
    if (currentNode) {
      const offScreen = !isElementInHorizontalScrollViewport(
        container,
        currentNode
      );
      if (isActiveOffScreenRef.current !== offScreen) {
        isActiveOffScreenRef.current = offScreen;
        onActiveOffScreenChangeRef.current?.(offScreen);
      }
      return;
    }

    if (isActiveOffScreenRef.current) {
      isActiveOffScreenRef.current = false;
      onActiveOffScreenChangeRef.current?.(false);
    }
  }, []);

  const centerOnActive = useCallback(() => {
    const container = scrollContainerRef.current;
    const currentNode = currentNodeRef.current;
    if (!container || !currentNode) return;
    const alignment =
      currentIndex === 0
        ? "start"
        : currentIndex === allNodes.length - 1
          ? "end"
          : "center";
    centerTimelineNodeInView(container, currentNode, alignment);
  }, [allNodes.length, currentIndex]);

  useImperativeHandle(ref, () => ({ centerOnActive }), [centerOnActive]);

  const getActiveNodeScrollAlignment = useCallback((): "start" | "end" | "center" => {
    if (currentIndex === 0) return "start";
    if (currentIndex === allNodes.length - 1) return "end";
    return "center";
  }, [allNodes.length, currentIndex]);

  const scrollActiveNodeIntoView = useCallback(
    (behavior: ScrollBehavior) => {
      const container = scrollContainerRef.current;
      const currentNode = currentNodeRef.current;
      if (!container || !currentNode) return;
      scrollTimelineNodeToCenter(
        container,
        currentNode,
        behavior,
        getActiveNodeScrollAlignment()
      );
    },
    [getActiveNodeScrollAlignment]
  );

  const ensureActiveScrollAlignment = useCallback(() => {
    const container = scrollContainerRef.current;
    const currentNode = currentNodeRef.current;
    if (!container || !currentNode || allNodes.length === 0) return false;

    const alignment = getActiveNodeScrollAlignment();
    const targetScrollLeft = getTimelineNodeTargetScrollLeft(
      container,
      currentNode,
      alignment
    );
    const needsScroll = Math.abs(container.scrollLeft - targetScrollLeft) > 1;

    if (needsScroll) {
      scrollActiveNodeIntoView("auto");
      return true;
    }
    return false;
  }, [
    allNodes.length,
    getActiveNodeScrollAlignment,
    scrollActiveNodeIntoView,
  ]);

  const handleScrollStep = useCallback(
    (direction: "left" | "right") => {
      const container = scrollContainerRef.current;
      if (!container) return;
      scrollTimelineByOneStep(container, direction);
    },
    []
  );

  const hasScrollOverflow =
    scrollOverflow.canScrollLeft || scrollOverflow.canScrollRight;

  useLayoutEffect(() => {
    if (currentIndex < 0) {
      currentNodeRef.current = null;
    }
  }, [currentIndex]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const currentNode = currentNodeRef.current;

    const isNewTarget = prevScrollKeyRef.current !== timelineStripScrollKey;
    if (isNewTarget) {
      prevScrollKeyRef.current = timelineStripScrollKey;
      if (!container || !currentNode || allNodes.length === 0) {
        updateScrollState();
        return;
      }
      scrollActiveNodeIntoView("auto");
      updateScrollState();
      return;
    }

    if (!container || !currentNode || allNodes.length === 0) {
      updateScrollState();
      return;
    }

    if (!ensureActiveScrollAlignment()) {
      if (!isElementInHorizontalScrollViewport(container, currentNode)) {
        scrollActiveNodeIntoView("auto");
      }
    }
    updateScrollState();
  }, [
    allNodes.length,
    ensureActiveScrollAlignment,
    scrollActiveNodeIntoView,
    timelineStripScrollKey,
    updateScrollState,
  ]);

  useLayoutEffect(() => {
    ensureActiveScrollAlignment();
  }, [hasScrollOverflow, ensureActiveScrollAlignment]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();

    const handleScroll = () => updateScrollState();
    container.addEventListener("scroll", handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      ensureActiveScrollAlignment();
      updateScrollState();
    });
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [allNodes.length, ensureActiveScrollAlignment, updateScrollState]);

  const handleMouseEnter = (
    item: TimelineItem,
    inboundEntry: InboundChange | undefined,
    event: React.MouseEvent<HTMLElement>
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoveredNode({
      item,
      inboundEntry,
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    setHoveredNode(null);
  };

  const handleBranchSelect = (branch: BranchInfo) => {
    setBranchPopoverOpen(false);
    onNodeClick(branch.firstItem);
  };

  const handleDiamondActivate = (
    item: TimelineItem,
    inboundEntry: InboundChange,
    flatTimelineIndex: number,
    event?: React.MouseEvent
  ) => {
    if (!item.accessionNumber || !onOpenConsolidation) return;
    event?.stopPropagation();
    setHoveredNode(null);
    onOpenConsolidation(
      item.accessionNumber,
      {
        asOf: resolveConsolidationAsOfForInbound(inboundEntry),
        flatTimelineIndex,
      },
      event
    );
  };

  if (allNodes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-24 text-sm text-muted-foreground">
        Ingen tidslinje tilgængelig
      </div>
    );
  }

  return (
    <div
      className={`relative flex min-h-24 min-w-0 flex-1 items-center py-1 ${
        compareMode
          ? "rounded-md ring-1 ring-amber-300/50 bg-amber-50/30 dark:bg-amber-900/10"
          : ""
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center">
        {hasScrollOverflow ? (
          <div className="flex h-8 shrink-0 items-center">
            <button
              type="button"
              className={`shrink-0 rounded-md p-1 transition-colors ${
                scrollOverflow.canScrollLeft
                  ? "hover:bg-muted text-muted-foreground hover:text-foreground"
                  : "text-muted-foreground/30 cursor-not-allowed"
              }`}
              onClick={() => handleScrollStep("left")}
              disabled={!scrollOverflow.canScrollLeft}
              aria-label={
                scrollOverflow.canScrollLeft
                  ? "Vis tidligere versioner"
                  : "Ingen tidligere versioner at vise"
              }
              tabIndex={scrollOverflow.canScrollLeft ? 0 : -1}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        <div className="relative min-w-0 flex-1">
          {scrollOverflow.canScrollLeft ? (
            <div
              className="pointer-events-none absolute top-0 bottom-2.5 left-0 z-10 w-6 bg-linear-to-r from-background to-transparent dark:from-muted"
              aria-hidden
            />
          ) : null}
          {scrollOverflow.canScrollRight ? (
            <div
              className="pointer-events-none absolute top-0 bottom-2.5 right-0 z-10 w-6 bg-linear-to-l from-background to-transparent dark:from-muted"
              aria-hidden
            />
          ) : null}

          <div
            ref={scrollContainerRef}
            role="group"
            aria-label="Dokumenttidslinje"
            className="themed-scrollbar timeline-strip-scroll min-w-0 overflow-x-auto overflow-y-hidden"
          >
          <div
            className={`flex w-max flex-col px-2 ${
              showConsolidationDiamonds
                ? TIMELINE_DIAMOND_ROW_PADDING_CLASS
                : TIMELINE_DEFAULT_ROW_PADDING_CLASS
            }`}
          >
            <div className="flex items-start">
            {allNodes.map((item, index) => {
              const flatIndex = flatTimeline.indexOf(item);
              const circleFill = getCircleFillClass(
                item.documentTypeId,
                flatIndex >= 0 ? flatIndex : 0
              );
              const isLast = index === allNodes.length - 1;
              const isOutOfOrder = item.isOutOfOrder;
              const isBranchPoint =
                branchPoint &&
                branchPoint.item.accessionNumber === item.accessionNumber;
              const isCompareTarget =
                compareMode &&
                item.accessionNumber === compareTargetAccessionNumber;
              const isCurrentInCompareMode =
                compareMode &&
                item.accessionNumber === currentAccessionNumber;
              const isAfterCurrent = compareMode && index > currentIndex;
              const isDisabledInCompare = eligibleCompareAccessions
                ? compareMode &&
                  (isCurrentInCompareMode ||
                    !eligibleCompareAccessions.has(item.accessionNumber ?? ""))
                : isCurrentInCompareMode || isAfterCurrent;
              const isDimmedInCompare = eligibleCompareAccessions
                ? isDisabledInCompare && !isCurrentInCompareMode
                : isAfterCurrent;
              const inboundEntry =
                flatIndex >= 0
                  ? inboundByAnchor
                    ? resolveInboundForTimelineNodeAcrossWindows(
                        flatTimeline,
                        flatIndex,
                        inboundByAnchor
                      )
                    : resolveInboundForTimelineNode(
                        flatTimeline,
                        flatIndex,
                        inboundChanges,
                        anchorLawAccession
                      )
                  : undefined;
              const showDiamond =
                showConsolidationDiamonds && Boolean(inboundEntry);
              const circleSelected = isCircleSelected(
                timelineSelection,
                item.accessionNumber,
                flatIndex >= 0 ? flatIndex : undefined
              );
              const diamondSelected = isDiamondSelected(
                timelineSelection,
                item.accessionNumber,
                flatIndex >= 0 ? flatIndex : undefined
              );
              const nodeLabel = item.shortName || item.title;
              const isHighlighted = Boolean(
                item.accessionNumber &&
                  highlightedAccessions?.has(item.accessionNumber)
              );
              const circleSizeClass =
                circleSelected || isCompareTarget || isCurrentInCompareMode
                  ? "size-4"
                  : "size-3";
              const circleOpacityClass = isDimmedInCompare
                ? "opacity-40"
                : isCurrentInCompareMode
                  ? "opacity-50"
                  : "";
              const circleRingClass = isCompareTarget
                ? TIMELINE_COMPARE_RING
                : circleSelected
                  ? TIMELINE_SELECTION_RING
                  : "";

              return (
                <div
                  key={`${item.accessionNumber || "node"}-${flatIndex}-${index}`}
                  ref={index === currentIndex ? currentNodeRef : undefined}
                  className="flex items-start"
                >
                  <div
                    className={`flex w-10 shrink-0 flex-col items-center ${
                      isHighlighted ? "rounded-md bg-primary/10" : ""
                    }`}
                  >
                    <div className="relative flex h-8 w-full items-center justify-center">
                      {showConsolidationDiamonds &&
                        showDiamond &&
                        inboundEntry &&
                        (compareMode ? (
                          <span
                            className={`absolute bottom-full left-1/2 mb-1.5 flex -translate-x-1/2 items-center justify-center p-0.5 ${
                              isDimmedInCompare ? "opacity-40" : ""
                            }`}
                            aria-hidden
                          >
                            <span
                              className={`block size-2.5 rotate-45 ${getDiamondFillClass(
                                inboundEntry.status
                              )}`}
                            />
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="absolute bottom-full left-1/2 mb-1.5 flex -translate-x-1/2 cursor-pointer items-center justify-center rounded-sm p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            aria-label={
                              inboundEntry.status === "APPROVED"
                                ? `Åbn verificeret konsolidering for ${nodeLabel}`
                                : `Åbn konsolideringsudkast for ${nodeLabel}`
                            }
                            onClick={(e) => {
                              handleDiamondActivate(
                                item,
                                inboundEntry,
                                flatIndex,
                                e
                              );
                            }}
                            onMouseEnter={(e) =>
                              handleMouseEnter(item, inboundEntry, e)
                            }
                            onMouseLeave={handleMouseLeave}
                          >
                            <span
                              className={`block size-2.5 rotate-45 transition-all ${getDiamondFillClass(
                                inboundEntry.status
                              )} ${
                                diamondSelected ? TIMELINE_SELECTION_RING : ""
                              }`}
                            />
                          </button>
                        ))}

                      <button
                        type="button"
                        className={`rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${circleSizeClass} ${
                          isOutOfOrder
                            ? TIMELINE_OUT_OF_ORDER_CIRCLE
                            : circleFill
                        } ${circleRingClass} ${circleOpacityClass} ${
                          isDisabledInCompare
                            ? "cursor-not-allowed"
                            : "cursor-pointer hover:brightness-95"
                        }`}
                        disabled={isDisabledInCompare}
                        aria-current={circleSelected ? "true" : undefined}
                        aria-label={
                          compareMode && !isDisabledInCompare
                            ? `Sammenlign med ${nodeLabel}`
                            : `Åbn lovtekst for ${nodeLabel}`
                        }
                        onClick={(e) => {
                          if (isDisabledInCompare) return;
                          setHoveredNode(null);
                          onNodeClick(
                            item,
                            e,
                            flatIndex >= 0 ? flatIndex : undefined
                          );
                        }}
                        onMouseEnter={(e) =>
                          handleMouseEnter(item, inboundEntry, e)
                        }
                        onMouseLeave={handleMouseLeave}
                      />
                    </div>

                    <div className="mt-1.5 flex items-center justify-center gap-1">
                      <span
                        className={`text-xs whitespace-nowrap ${
                          isCompareTarget
                            ? "font-semibold text-amber-600"
                            : circleSelected || diamondSelected
                              ? "font-semibold text-foreground"
                              : isDimmedInCompare
                                ? "text-muted-foreground/40"
                                : "text-muted-foreground"
                        }`}
                      >
                        {item.year}
                      </span>

                      {isBranchPoint && branchPoint ? (
                        <Popover
                          open={branchPopoverOpen}
                          onOpenChange={setBranchPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <button
                              className="p-0.5 hover:bg-muted rounded transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              aria-label="Vælg gren"
                            >
                              <GitBranch className="h-3.5 w-3.5 text-primary" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-64 p-0"
                            align="start"
                            side="bottom"
                          >
                            <div className="p-3 border-b">
                              <div className="text-sm font-medium">
                                Vælg gren at udforske
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {branchPoint.branches.length} forgreninger
                              </div>
                            </div>
                            <div className="p-2 max-h-96 overflow-y-auto">
                              {branchPoint.branches.map(
                                (branch, branchIndex) => {
                                  const branchTitle = cleanTitle(
                                    branch.firstItem.title
                                  );

                                  return (
                                    <button
                                      key={
                                        branch.firstItem.accessionNumber ||
                                        branchIndex
                                      }
                                      className={`w-full text-left px-3 py-2.5 rounded-md text-sm hover:bg-muted transition-colors flex items-start gap-3 ${
                                        branch.isCurrentPath
                                          ? "bg-primary/10"
                                          : ""
                                      }`}
                                      onClick={() => handleBranchSelect(branch)}
                                    >
                                      <div className="flex flex-col min-w-0 flex-1 gap-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {branch.firstItem.shortName && (
                                            <span className="font-semibold text-sm text-foreground">
                                              {branch.firstItem.shortName}
                                            </span>
                                          )}
                                          {branch.isCurrentPath && (
                                            <Badge
                                              variant="secondary"
                                              className="shrink-0 text-xs"
                                            >
                                              aktiv
                                            </Badge>
                                          )}
                                        </div>
                                        <span className="text-xs text-muted-foreground leading-relaxed">
                                          {branchTitle}
                                        </span>
                                        <span className="text-xs text-muted-foreground/70">
                                          {branch.itemCount} version
                                          {branch.itemCount !== 1 ? "er" : ""}
                                        </span>
                                      </div>
                                    </button>
                                  );
                                }
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      ) : null}
                    </div>
                  </div>

                  {!isLast && (
                    <div className="flex h-8 shrink-0 items-center mx-2">
                      <div
                        className={`w-12 ${
                          isOutOfOrder || allNodes[index + 1]?.isOutOfOrder
                            ? "h-0 border-t-2 border-dashed border-muted-foreground/20"
                            : "h-0.5 bg-muted-foreground/20"
                        }`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            <div className="h-2.5 shrink-0" aria-hidden />
          </div>
        </div>
        </div>

        {hasScrollOverflow ? (
          <div className="flex h-8 shrink-0 items-center">
            <button
              type="button"
              className={`shrink-0 rounded-md p-1 transition-colors ${
                scrollOverflow.canScrollRight
                  ? "hover:bg-muted text-muted-foreground hover:text-foreground"
                  : "text-muted-foreground/30 cursor-not-allowed"
              }`}
              onClick={() => handleScrollStep("right")}
              disabled={!scrollOverflow.canScrollRight}
              aria-label={
                scrollOverflow.canScrollRight
                  ? "Vis senere versioner"
                  : "Ingen senere versioner at vise"
              }
              tabIndex={scrollOverflow.canScrollRight ? 0 : -1}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>

      {hoveredNode && !branchPopoverOpen && (
        <HoverCard
          item={hoveredNode.item}
          x={hoveredNode.x}
          y={hoveredNode.y}
          isCircleSelected={isCircleSelected(
            timelineSelection,
            hoveredNode.item.accessionNumber,
            flatTimeline.indexOf(hoveredNode.item)
          )}
          isDiamondSelected={isDiamondSelected(
            timelineSelection,
            hoveredNode.item.accessionNumber,
            flatTimeline.indexOf(hoveredNode.item)
          )}
          inboundEntry={hoveredNode.inboundEntry}
          lastVerifiedLabel={
            hoveredNode.item.accessionNumber === anchorLawAccession
              ? anchorConsolidationProgress?.lastVerifiedLabel ?? null
              : null
          }
        />
      )}
    </div>
  );
});

const HoverCard: React.FC<{
  item: TimelineItem;
  x: number;
  y: number;
  isCircleSelected: boolean;
  isDiamondSelected: boolean;
  inboundEntry?: InboundChange;
  lastVerifiedLabel?: string | null;
}> = ({
  item,
  x,
  y,
  isCircleSelected,
  isDiamondSelected,
  inboundEntry,
  lastVerifiedLabel,
}) => {
  const date = parseSignatureDate(item.signatureDate);
  const dateStr = date ? formatDanishDate(date) : item.signatureDate;
  const title = cleanTitle(item.title);
  const verifiedDateLabel = formatVerifiedDate(inboundEntry?.approvedAt);

  const estimatedHeight = 140;
  const showBelow = y - estimatedHeight < 10;
  const nodeBottomY = y + 48;

  const style: React.CSSProperties = {
    position: "fixed",
    left: `${x}px`,
    top: showBelow ? `${nodeBottomY + 8}px` : `${y - 8}px`,
    transform: showBelow ? "translate(-50%, 0)" : "translate(-50%, -100%)",
    zIndex: 9999,
  };

  const content = (
    <div
      style={style}
      className="bg-primary text-primary-foreground rounded-lg shadow-lg px-4 py-3 max-w-xs pointer-events-none"
    >
      {showBelow && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
          style={{
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid hsl(var(--primary))",
          }}
        />
      )}

      <div className="text-base font-semibold mb-1">{dateStr}</div>
      <div className="text-sm opacity-90 whitespace-pre-line leading-snug">
        {title}
      </div>
      {item.shortName && (
        <div className="text-sm opacity-80 mt-1">{item.shortName}</div>
      )}

      {inboundEntry && verifiedDateLabel && inboundEntry.status === "APPROVED" && (
        <div className="text-xs opacity-80 mt-2">
          Verificeret pr. {verifiedDateLabel}
        </div>
      )}

      {lastVerifiedLabel && (
        <div className="text-xs opacity-80 mt-2">{lastVerifiedLabel}</div>
      )}

      {item.isOutOfOrder && (
        <div className="text-xs mt-2 font-medium opacity-90 bg-primary-foreground/20 rounded px-2 py-0.5 inline-block">
          {TIMELINE_OUT_OF_ORDER_LABEL}
        </div>
      )}

      {(isCircleSelected || isDiamondSelected) && (
        <div className="text-xs mt-2 font-medium opacity-90 bg-primary-foreground/20 rounded px-2 py-0.5 inline-block">
          {isDiamondSelected ? "Nuværende konsolidering" : "Nuværende dokument"}
        </div>
      )}

      {!showBelow && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0"
          style={{
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid hsl(var(--primary))",
          }}
        />
      )}
    </div>
  );

  return createPortal(content, document.body);
};

export default TimelineStrip;
